import React, { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { TEAM_ROLES, SALARY_CYCLES } from "../../utils/constants";
import { downloadPDF } from "../../utils/pdf";
import { formatINR, formatDate, today } from "../../utils/helpers";
import { safeCopy, exportCSV } from "../../utils/crypto";
import { Plus, X, UserMinus, RefreshCw, Copy, Check, Download, Trash2, AlertTriangle } from "lucide-react";

export default function Team() {
  const { team, addTeamMember, removeTeamMember, deleteTeamMember, regenerateCode } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [showCode, setShowCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ name:"", role:TEAM_ROLES[0], customRole:"", salary:"", salaryCycle:"Monthly", salaryDate:today(), salaryCurrency:"INR", joinDate:today(), notes:"" });

  useEffect(() => { const h = () => { setShowForm(false); setShowCode(null); }; window.addEventListener("closeModals", h); return () => window.removeEventListener("closeModals", h); }, []);

  const handleDelete = (id) => {
    if (confirmDelete === id) { deleteTeamMember(id); setConfirmDelete(null); }
    else { setConfirmDelete(id); setTimeout(() => setConfirmDelete(null), 3000); }
  };

  const resetForm = () => setForm({ name:"", role:TEAM_ROLES[0], customRole:"", salary:"", salaryCycle:"Monthly", salaryDate:today(), salaryCurrency:"INR", joinDate:today(), notes:"" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const role = form.role === "__other__" ? form.customRole : form.role;
    if (form.role === "__other__" && !form.customRole.trim()) return;
    const result = await addTeamMember({ ...form, role });
    if (result) { setShowCode(result.code); setShowForm(false); resetForm(); }
  };

  const handleRegenerate = async (id) => { const code = await regenerateCode(id); if (code) setShowCode(code); };
  const copyCode = async () => { const ok = await safeCopy(showCode); setCopied(ok); setTimeout(() => setCopied(false), 2000); };
  const handleExport = () => {
    exportCSV("finboard-team.csv", ["Name","Role","Salary","Cycle","Salary Date","Status","Joined"],
      team.map(m => [m.name, m.role, m.salary, m.salaryCycle||"Monthly", m.salaryDate||"", m.status, m.joinDate]));
  };

  const active = team.filter(m => m.status === "Active");
  const inactive = team.filter(m => m.status === "Inactive");

  
  const exportTeamPDF = () => {
    downloadPDF("Team Members Report",
      ["Name", "Role", "Salary", "Status", "Joined"],
      team.map(m => [m.name, m.role, formatINR(m.salary), m.status, formatDate(m.joinedDate || "—")]),
      [
        { label: "Total Members", value: team.length + "" },
        { label: "Active", value: team.filter(m => m.status === "Active").length + "" },
        { label: "Monthly Payroll", value: formatINR(team.filter(m => m.status === "Active").reduce((s,m) => s + Number(m.salary), 0)), color: "#3B82F6" },
      ]
    );
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h3>{active.length} Active Members</h3><span className="text-muted">Payroll: {formatINR(active.reduce((s,m) => s+m.salary, 0))}/mo</span></div>
        <div className="header-actions">
          {team.length > 0 && <button className="btn-ghost" onClick={handleExport}><Download size={14}/> CSV</button>}
          <button className="btn-ghost" onClick={exportTeamPDF} style={{display:"flex",alignItems:"center",gap:6}}><Download size={14}/> PDF</button>
        <button className="btn-gold" onClick={() => setShowForm(true)}><Plus size={16}/> Add Member</button>
        </div>
      </div>

      <div className="table-card">
        <table>
          <thead><tr><th>Name</th><th>Role</th><th>Salary</th><th>Next Salary</th><th>Joined</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {[...active,...inactive].map(m => (
              <tr key={m.id} className={m.status==="Inactive"?"row-inactive":""}>
                <td className="primary">{m.name}</td>
                <td className="text-muted">{m.role}</td>
                <td className="mono amount">{formatINR(m.salary)}<span style={{display:"block",fontSize:10,color:"var(--t5)"}}>{m.salaryCycle||"Monthly"}</span></td>
                <td className="mono">{m.salaryDate ? formatDate(m.salaryDate) : "Not set"}</td>
                <td className="mono">{formatDate(m.joinDate)}</td>
                <td><span className={"status-badge "+m.status.toLowerCase()}>{m.status}</span></td>
                <td className="actions-cell">
                  {m.status==="Active" && <>
                    <button className="icon-btn" title="New Code" onClick={() => handleRegenerate(m.id)}><RefreshCw size={14}/></button>
                    <button className="icon-btn danger" title="Deactivate" onClick={() => removeTeamMember(m.id)}><UserMinus size={14}/></button>
                  </>}
                  {confirmDelete === m.id ? (
                    <button className="btn-deny" style={{padding:"5px 12px",fontSize:11}} onClick={() => handleDelete(m.id)}>
                      <AlertTriangle size={13}/> Delete?
                    </button>
                  ) : (
                    <button className="icon-btn danger" title="Delete permanently" onClick={() => handleDelete(m.id)}><Trash2 size={14}/></button>
                  )}
                </td>
              </tr>
            ))}
            {team.length===0 && <tr><td colSpan={7} className="empty-row">No team members yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Add Team Member</h3><button className="icon-btn" onClick={() => setShowForm(false)}><X size={18}/></button></div>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Full Name</label><input type="text" value={form.name} onChange={e => setForm({...form,name:e.target.value})} placeholder="Employee name" maxLength={50} required/></div>
              <div className="form-row">
                <div className="form-group"><label>Role</label>
                  <select value={form.role} onChange={e => setForm({...form, role:e.target.value, customRole:""})}>
                    {TEAM_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    <option value="__other__">Other (Custom)</option>
                  </select>
                </div>
                <div className="form-group"><label>Monthly Salary</label><input type="number" value={form.salary} onChange={e => setForm({...form,salary:e.target.value})} placeholder="0" min="1" required/></div>
              </div>
              {form.role === "__other__" && (
                <div className="form-group"><label>Custom Role Name</label><input type="text" value={form.customRole} onChange={e => setForm({...form,customRole:e.target.value})} placeholder="Enter role name" maxLength={30} required/></div>
              )}
              <div className="form-row">
                <div className="form-group"><label>Salary Cycle</label>
                  <select value={form.salaryCycle} onChange={e => setForm({...form,salaryCycle:e.target.value})}>
                    {SALARY_CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Next Salary Date</label><input type="date" value={form.salaryDate} onChange={e => setForm({...form,salaryDate:e.target.value})} required/></div>
              </div>
              <div className="form-group"><label>Join Date</label><input type="date" value={form.joinDate} onChange={e => setForm({...form,joinDate:e.target.value})}/></div>
              <div className="form-group"><label>Notes</label><input type="text" value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} placeholder="Optional notes" maxLength={200}/></div>
              <button type="submit" className="btn-gold full">Add Member & Generate Code</button>
            </form>
          </div>
        </div>
      )}

      {showCode && (
        <div className="modal-overlay" onClick={() => {setShowCode(null);setCopied(false);}}>
          <div className="modal code-modal" onClick={e => e.stopPropagation()}>
            <div className="code-reveal">
              <h3>Employee Access Code</h3>
              <p className="code-warning">This code will NOT be shown again!</p>
              <div className="code-display"><span>{showCode}</span>
                <button onClick={copyCode} className="copy-btn">{copied?<Check size={16}/>:<Copy size={16}/>}{copied?"Copied!":"Copy"}</button>
              </div>
              <button className="btn-gold full" onClick={() => {setShowCode(null);setCopied(false);}}>I've Saved This Code</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
